import {useState} from "react";
import className from "classnames/bind";
import style from "../style/Gsc.module.scss";

const cn = className.bind(style);

const CLICK_COLOR = '#4f46e5';
const IMPR_COLOR  = '#0ea5e9';

const fmt = (n) => Number(n || 0).toLocaleString('vi-VN');

/**
 * Đường cong trơn (Catmull-Rom → Bézier) qua danh sách điểm {x,y}. Hệ số 6 = độ căng
 * chuẩn, cho nét mềm mà không "vượt" (overshoot) khỏi dữ liệu.
 */
function smoothPath(pts) {
	if (pts.length < 2) return '';

	let d = `M ${pts[0].x},${pts[0].y}`;

	for (let i = 0; i < pts.length - 1; i++) {
		const p0 = pts[i - 1] || pts[i];
		const p1 = pts[i];
		const p2 = pts[i + 1];
		const p3 = pts[i + 2] || p2;

		const cp1x = p1.x + (p2.x - p0.x) / 6;
		const cp1y = p1.y + (p2.y - p0.y) / 6;
		const cp2x = p2.x - (p3.x - p1.x) / 6;
		const cp2y = p2.y - (p3.y - p1.y) / 6;

		d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
	}

	return d;
}

/**
 * Biểu đồ xu hướng clicks + impressions theo ngày (SVG thuần, không thêm dependency).
 * Đường cong trơn + vùng tô gradient dưới mỗi đường. Di chuột lên biểu đồ hiện đường dẫn
 * dọc + tooltip số liệu của ngày đó. Hai đường chuẩn hoá theo max RIÊNG (thang trục khác
 * nhau) — đọc xu hướng, không so trực tiếp trị tuyệt đối. series: [{date, clicks, impressions, ctr, position}].
 */
function GscTrendChart({series = []}) {

	const [hover, setHover] = useState(null);

	if (!series.length) {
		return <div className={cn('chartEmpty')}>Chưa có dữ liệu trong khoảng thời gian này. Nếu vừa kết nối, dữ liệu đang được đồng bộ trong nền.</div>;
	}

	const W = 720;
	const H = 240;
	const P = 26;
	const innerW = W - P * 2;
	const innerH = H - P * 2;
	const baseY  = P + innerH;

	const n = series.length;
	const maxClicks = Math.max(1, ...series.map((d) => d.clicks));
	const maxImpr = Math.max(1, ...series.map((d) => d.impressions));

	const x = (i) => (n === 1 ? W / 2 : P + (innerW * i) / (n - 1));
	const y = (v, max) => P + innerH - (innerH * v) / max;

	const clickPts = series.map((d, i) => ({x: x(i), y: y(d.clicks, maxClicks)}));
	const imprPts  = series.map((d, i) => ({x: x(i), y: y(d.impressions, maxImpr)}));

	const clickLine = smoothPath(clickPts);
	const imprLine  = smoothPath(imprPts);

	// Vùng tô = đường cong + khép xuống đáy.
	const area = (line, pts) =>
		`${line} L ${pts[pts.length - 1].x},${baseY} L ${pts[0].x},${baseY} Z`;

	// Nhãn trục X: đầu / giữa / cuối.
	const ticks = n === 1 ? [0] : [0, Math.floor((n - 1) / 2), n - 1];

	const last = n - 1;

	// Chuột → chỉ số ngày gần nhất (svg bị kéo giãn nên tính theo tỉ lệ của phần tử bọc).
	const handleMove = (e) => {
		if (n < 1) return;
		const rect = e.currentTarget.getBoundingClientRect();
		const frac = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
		setHover(n === 1 ? 0 : Math.round(frac * (n - 1)));
	};

	const hoverPct = hover === null ? 0 : (n === 1 ? 50 : (hover / (n - 1)) * 100);

	return (
		<div className={cn('chart')}>
			<div className={cn('legend')}>
				<span className={cn('legendItem')}><span className={cn('dot')} style={{background: CLICK_COLOR}} /> Clicks (tối đa {fmt(maxClicks)})</span>
				<span className={cn('legendItem')}><span className={cn('dot')} style={{background: IMPR_COLOR}} /> Impressions (tối đa {fmt(maxImpr)})</span>
			</div>

			<div className={cn('plotWrap')} onMouseMove={handleMove} onMouseLeave={() => setHover(null)}>
				<svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
					<defs>
						<linearGradient id="gscClickGrad" x1="0" y1="0" x2="0" y2="1">
							<stop offset="0%" stopColor={CLICK_COLOR} stopOpacity="0.28" />
							<stop offset="100%" stopColor={CLICK_COLOR} stopOpacity="0" />
						</linearGradient>
						<linearGradient id="gscImprGrad" x1="0" y1="0" x2="0" y2="1">
							<stop offset="0%" stopColor={IMPR_COLOR} stopOpacity="0.20" />
							<stop offset="100%" stopColor={IMPR_COLOR} stopOpacity="0" />
						</linearGradient>
					</defs>

					{/* Lưới ngang */}
					{[0, 0.5, 1].map((g) => (
						<line key={g} x1={P} y1={P + innerH * (1 - g)} x2={W - P} y2={P + innerH * (1 - g)}
							stroke="#eef0f4" strokeWidth="1" vectorEffect="non-scaling-stroke" />
					))}

					{n > 1 && (
						<>
							{/* Impressions (nền) */}
							<path d={area(imprLine, imprPts)} fill="url(#gscImprGrad)" />
							<path d={imprLine} fill="none" stroke={IMPR_COLOR} strokeWidth="2"
								strokeLinecap="round" strokeLinejoin="round" opacity="0.9"
								vectorEffect="non-scaling-stroke" />

							{/* Clicks (trên) */}
							<path d={area(clickLine, clickPts)} fill="url(#gscClickGrad)" />
							<path d={clickLine} fill="none" stroke={CLICK_COLOR} strokeWidth="2.5"
								strokeLinecap="round" strokeLinejoin="round"
								vectorEffect="non-scaling-stroke" />

							{/* Điểm cuối nổi bật (khi không hover) */}
							{hover === null && (
								<>
									<circle cx={clickPts[last].x} cy={clickPts[last].y} r="3.5" fill="#fff" stroke={CLICK_COLOR} strokeWidth="2" vectorEffect="non-scaling-stroke" />
									<circle cx={imprPts[last].x} cy={imprPts[last].y} r="3" fill="#fff" stroke={IMPR_COLOR} strokeWidth="2" vectorEffect="non-scaling-stroke" />
								</>
							)}

							{/* Đường dẫn dọc + điểm khi hover */}
							{hover !== null && (
								<>
									<line x1={x(hover)} y1={P} x2={x(hover)} y2={baseY}
										stroke="#94a3b8" strokeWidth="1" strokeDasharray="4 3" vectorEffect="non-scaling-stroke" />
									<circle cx={imprPts[hover].x} cy={imprPts[hover].y} r="3.5" fill="#fff" stroke={IMPR_COLOR} strokeWidth="2" vectorEffect="non-scaling-stroke" />
									<circle cx={clickPts[hover].x} cy={clickPts[hover].y} r="4" fill="#fff" stroke={CLICK_COLOR} strokeWidth="2" vectorEffect="non-scaling-stroke" />
								</>
							)}
						</>
					)}

					{n === 1 && (
						<>
							<circle cx={x(0)} cy={y(series[0].impressions, maxImpr)} r="4" fill={IMPR_COLOR} />
							<circle cx={x(0)} cy={y(series[0].clicks, maxClicks)} r="4" fill={CLICK_COLOR} />
						</>
					)}

					{ticks.map((i) => (
						<text key={i} x={x(i)} y={H - 6} fontSize="10" fill="#9ca3af"
							textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}>
							{String(series[i].date).slice(5)}
						</text>
					))}
				</svg>

				{/* Tooltip số liệu ngày đang hover */}
				{hover !== null && (
					<div className={cn('tooltip')} style={{left: `${hoverPct}%`}}>
						<div className={cn('tooltipDate')}>{series[hover].date}</div>
						<div className={cn('tooltipRow')}>
							<span><span className={cn('dot')} style={{background: CLICK_COLOR}} /> Clicks</span>
							<b>{fmt(series[hover].clicks)}</b>
						</div>
						<div className={cn('tooltipRow')}>
							<span><span className={cn('dot')} style={{background: IMPR_COLOR}} /> Impressions</span>
							<b>{fmt(series[hover].impressions)}</b>
						</div>
						<div className={cn('tooltipRow')}>
							<span>CTR</span>
							<b>{((series[hover].ctr || 0) * 100).toFixed(1)}%</b>
						</div>
						<div className={cn('tooltipRow')}>
							<span>Vị trí</span>
							<b>{Number(series[hover].position || 0).toFixed(1)}</b>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}

export default GscTrendChart;
